<template>
  <bm-control v-bind="$attrs" :read-only-value="formattedReadOnlyValue">
    <template #default="{ dataQa }">
      <v-text-field
        class="bm-text"
        :persistent-hint="true"
        :placeholder="placeholderVal"
        :value="value"
        :prefix="prefix"
        :suffix="truncatedSuffix"
        v-bind="$attrs"
        color="brand02"
        :label="undefined"
        :data-qa="dataQa"
        :data-component="dataComponent"
        @input="handleEvent($event)"
        @focus="bubbleEvent($event)"
        @blur="blurEvent($event)"
        @keydown="bubbleEvent($event)"
        @mousedown="bubbleEvent($event)"
        @touchstart="bubbleEvent($event)"
        @click:append="bubbleCustomEvent('click:append', $event)"
      >
        <template #append-outer>
          <slot name="append-outer" />
        </template>
        <template #append>
          <slot name="append" />
        </template>
        <template #prepend-inner>
          <slot name="prepend-inner" />
        </template>
        <template #prepend>
          <slot name="prepend" />
        </template>
      </v-text-field>
    </template>
    <template v-for="(_, slot) of $scopedSlots" #[slot]="scope"
      ><slot :name="slot" v-bind="scope"
    /></template>
  </bm-control>
</template>

<script lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import { useEventBubbling } from "@/components/controls/input/mixins/BmInputField";
import { computed, defineComponent } from "vue";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";
import truncate from "lodash-es/truncate";

export default defineComponent({
  name: "BmText",
  components: { BmControl },
  props: {
    autoTrim: { type: Boolean, default: true },
    value: { type: String },
    placeholder: { type: String },
    prefix: { type: String },
    suffix: { type: String },
    readOnlyValue: { type: String },
    maxSuffixLength: {
      type: Number,
      required: false,
    },
  },
  setup(props, context) {
    const { bubbleEvent, bubbleCustomEvent } = useEventBubbling(context);

    const formattedReadOnlyValue = computed(() => {
      const value = props.readOnlyValue ? props.readOnlyValue : props.value;

      if (value) {
        return (
          (props.prefix ? props.prefix + " " : "") +
          value +
          (props.suffix ? " " + props.suffix : "")
        );
      } else {
        return value;
      }
    });

    // This is ugly, but it makes all v-text-field controls think they have a placeholder.
    // Which in turn causes the label to render above the field.
    const placeholderVal = computed(() => {
      if (!props.placeholder) {
        return " ";
      }

      return props.placeholder;
    });

    /**
     * Auto trim on blur (if desired)
     */
    const blurEvent = (event: FocusEvent) => {
      if (props.autoTrim && props.value && props.value.trim() !== props.value) {
        context.emit("input", props.value.trim());
      }

      bubbleEvent(event);
    };

    const truncatedSuffix = computed(() => {
      if (props.maxSuffixLength) {
        return truncate(props.suffix, {
          length: props.maxSuffixLength,
        });
      }

      return props.suffix;
    });

    return {
      formattedReadOnlyValue,
      placeholderVal,
      blurEvent,
      bubbleEvent,
      handleEvent: (newVal: string) => {
        context.emit("input", newVal);
      },
      bubbleCustomEvent,
      dataComponent: dataComponentAttrs.bmText,
      truncatedSuffix,
    };
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"

:deep(.bm-label)
  @include bm-field-label

.bm-text
  @include bm-field
</style>
